/** @file The IMPOSTOR_LAYOUT fields of one visible impostor from its pick, pose and view (design 21). */
import type { FacetPick } from '@zilo/pose3d-facet/contract';
import type { ImpostorDrawable } from '@zilo/pose3d-format/types/drawable';
import type { RotateMode } from '@zilo/pose3d-format/types/facet';
import type { Profile } from '@zilo/pose3d-format/types/profile';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import type { PoseBuffer } from '@zilo/pose3d-pose/contract';
import { clamp } from '@zilo/pose3d-std/math/clamp';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import { screenOf } from '@zilo/pose3d-render-core/camera/screenOf';
import type { View } from '@zilo/pose3d-render-core/camera/viewOf';
import { displayPivot } from '@zilo/pose3d-render-core/cut/displayPivot';
import { entryDepthOffsetOf } from '@zilo/pose3d-render-core/depth/entryDepthOffsetOf';
import { zBiasOf } from '@zilo/pose3d-render-core/depth/zBiasOf';
import { RENDER_CORE_ERRORS } from '@zilo/pose3d-render-core/errors/renderCoreErrors';
import { anchorPointOf } from '@zilo/pose3d-render-core/place/anchorPointOf';
import { cutSlotsOf } from '@zilo/pose3d-render-core/place/cutSlotsOf';
import { entityPointOf } from '@zilo/pose3d-render-core/place/entityPointOf';
import { groundRotationOf } from '@zilo/pose3d-render-core/place/groundRotationOf';
import { lerpPlacementOf, type NodePlacement } from '@zilo/pose3d-render-core/place/lerpPlacementOf';
import { nodePointsOf } from '@zilo/pose3d-render-core/place/nodePointsOf';
import type { PartMap } from '@zilo/pose3d-render-core/place/partMapOf';
import { poseNodeOf } from '@zilo/pose3d-render-core/place/poseNodeOf';
import { screenRotationOf } from '@zilo/pose3d-render-core/place/screenRotationOf';
import { sheetIndexOf } from '@zilo/pose3d-render-core/place/sheetIndexOf';
import type { ImpostorFields } from '@zilo/pose3d-render-core/place/writeImpostor';

/** What the per-mode rules read for one impostor; `parts` is the host part map of the drawable (22.3), absent without one. */
export type PlaceContext = {
  puppet: Puppet; pose: PoseBuffer; view: View; profile: Profile; cameraYawDeg: number; entityPos: Vec3;
  nodeIndex: number; drawable: ImpostorDrawable; drawableIndex: number; pick: FacetPick;
  nodeIndices: Map<string, number>; zScaleRotate: number; parts?: PartMap;
};

/** The fields a rotate mode decides; the rest come from the pick. */
type ModeFields = Pick<ImpostorFields, 'b' | 'rotate' | 'flags' | 'cutAngle' | 'cutPos' | 'cutMode' | 'cutOff'>;

/** The placed points a mode reads: absolute A, the blended placement (entity-relative), the display pivot. */
type Placed = { a: Vec3; rel: NodePlacement; pivot: [number, number] };

const NEUTRAL = { cutAngle: 0, cutPos: 0, cutMode: 0, cutOff: 0 } as const;

/** The two-point path (21.3) applies only to `screen` / `parent` entries whose cut is `cut` or `scale`. */
const isTwoPoint = (pick: FacetPick): boolean => (pick.rotate === 'screen' || pick.rotate === 'parent') && pick.cut !== 'none';

/** One-point (21.3) or two-point (21.6, pivot2 inset `cutOff` = `anchor.pixelOffset`) placement of a bone entry. */
function bonePartOf(ctx: PlaceContext, p: Placed): ModeFields {
  const b = p.rel.b;
  const seg = b === undefined ? 0 : Math.hypot(b[0] - p.rel.a[0], b[1] - p.rel.a[1], b[2] - p.rel.a[2]);
  if (b === undefined || seg < 1e-12) return { ...NEUTRAL, b: p.a, rotate: p.rel.theta, flags: 0 };
  const bAbs = entityPointOf(b, ctx.entityPos, ctx.view);
  const { pick } = ctx;
  const cutOff = ctx.drawable.anchor?.pixelOffset ?? 0;
  const cut = cutSlotsOf({
    cut: pick.cut, inverse: pick.cutInverse, pivot: p.pivot, size: [pick.rect[2], pick.rect[3]], drawnScreenAngleDeg: pick.drawnScreenAngleDeg,
    aScreen: screenOf(p.a, ctx.view), bScreen: screenOf(bAbs, ctx.view), viewport: ctx.view.viewport, cutOff,
  });
  return { ...cut, cutOff, b: bAbs, rotate: pick.drawnScreenAngleDeg, flags: 4 | 16 };
}

/** A rule per rotate mode (`pick.rotate`, already effective after rotateOff / rotToggle, 18.10). */
const BY_MODE: Record<RotateMode, (ctx: PlaceContext, p: Placed) => ModeFields> = {
  none: (_ctx, p) => ({ ...NEUTRAL, b: p.a, rotate: 0, flags: 0 }),
  ground: (ctx, p) => ({
    ...NEUTRAL, b: p.a, rotate: groundRotationOf(poseNodeOf(ctx.pose, ctx.nodeIndex).yaw, ctx.cameraYawDeg, ctx.pick.drawnAzimuthDeg), flags: 8,
  }),
  screen: bonePartOf,
  parent: bonePartOf,
};

/**
 * One node's placement: the rounded drawable point A, the one-point theta of its own bone, and the second point B of
 * the two-point path (`screen`: the rounded node itself; `parent`: `anchorPointOf`).
 */
function placementOn(ctx: PlaceContext, nodeIndex: number, nodeName: string): NodePlacement {
  const { pick } = ctx;
  const parts = ctx.parts === undefined ? undefined : { map: ctx.parts, name: nodeName };
  const pts = nodePointsOf(ctx.pose, nodeIndex, ctx.drawable.offset, ctx.view, ctx.zScaleRotate, parts);
  const theta = screenRotationOf(pts, ctx.view, pick.drawnScreenAngleDeg);
  if (!isTwoPoint(pick)) return { a: pts.a, theta, b: undefined };
  const b = pick.rotate === 'screen' ? pts.n : anchorPointOf(ctx.pose, pts, ctx.drawable.anchor, ctx.nodeIndices, ctx.view, ctx.zScaleRotate);
  return { a: pts.a, theta, b };
}

/** The drawable's placement, blended toward `drawable.lerp.node` when it has one (21.7). */
function placementOf(ctx: PlaceContext): NodePlacement {
  const own = placementOn(ctx, ctx.nodeIndex, ctx.drawable.node);
  const lerp = ctx.drawable.lerp;
  if (lerp === undefined) return own;
  const m = ctx.nodeIndices.get(lerp.node) ?? throwError(RENDER_CORE_ERRORS.NODE_UNKNOWN, { node: lerp.node });
  return lerpPlacementOf(own, placementOn(ctx, m, lerp.node), lerp, ctx.zScaleRotate);
}

/**
 * Every slot of the impostor `ctx.drawable` for `ctx.pick`. With the host part map (22.3) A and anchor-offset second
 * points are moved once before rounding; corners, rotation and cuts follow from the moved points. zBias adds the
 * entry's `depth.offset` (22.4).
 */
export function impostorFieldsOf(ctx: PlaceContext): ImpostorFields {
  const { pick, drawable, profile } = ctx;
  const rel = placementOf(ctx);
  const a = entityPointOf(rel.a, ctx.entityPos, ctx.view);
  const pivot = displayPivot(pick.pivot, pick.rect[2], pick.flip, profile.snap, profile.halfPixel);
  const mode = BY_MODE[pick.rotate](ctx, { a, rel, pivot });
  return {
    ...mode, a, pivot, tex: [pick.rect[0], pick.rect[1], pick.rect[2], pick.rect[3]], scaleX: 1, scaleY: 1,
    sheer: clamp(drawable.sheer, 0, 2), zBias: zBiasOf(drawable.zOrder, pick.depthOffset, entryDepthOffsetOf(drawable, pick.entry)),
    flags: mode.flags | (pick.flip ? 1 : 0),
    sheet: sheetIndexOf(ctx.puppet, pick.sheet), drawable: ctx.drawableIndex,
  };
}
